import { useEffect, useState } from 'react';
import { ENV } from '../amplify-config';
import { activePortalApiBaseUrl } from './zone-session';
import type { ZoneMap } from '../obw/residency';

/**
 * zone-map.ts — the MOUNT-side reads the residency step depends on (ADR-124 I2, I8). The step
 * itself holds no transport (ADR-107 §2.4); its mounts call these and hand the results
 * to the journey as data and as a port.
 */

/** The sow-gate Worker origin, derived from the gate-state URL the bundle already carries. Read
 *  at call time: a bundle (or test) without it reads every gate as closed, never crashes. */
export const sowGateBase = (): string => (ENV.SOW_GATE_STATE_URL ?? '').replace(/\/gate\/state$/, '');

/**
 * ⚠ ANY zone's portal API serves the same bytes (the map is compiled from one table), so the
 * ACTIVE zone — the build zone before sign-in — is correct here by construction, not by default.
 */
let inflight: Promise<ZoneMap | null> | null = null;

/** Is this the shape `GET /portal/zone-map` serves? A malformed body is treated as no map. */
function isZoneMap(v: unknown): v is ZoneMap {
  if (!v || typeof v !== 'object') return false;
  const m = v as Partial<ZoneMap>;
  return !!m.countries && typeof m.countries === 'object' && !!m.zones && typeof m.zones === 'object'
    && Object.keys(m.countries).length > 0;
}

/**
 * The served country → zone map (public, identical in every zone, cached for the page's life).
 * Resolves `null` on any failure — never a partial or guessed map — so the step can say it could
 * not load and refuse to continue (a country that cannot be placed cannot be minted).
 */
export function loadZoneMap(): Promise<ZoneMap | null> {
  if (!inflight) {
    inflight = (async () => {
      try {
        const res = await fetch(`${activePortalApiBaseUrl()}/portal/zone-map`);
        if (!res.ok) throw new Error(`zone-map ${res.status}`);
        const body = (await res.json()) as unknown;
        if (!isZoneMap(body)) throw new Error('zone-map: unexpected shape');
        return body;
      } catch (err) {
        console.error('zone-map: load failed', err);
        inflight = null; // a later mount may retry
        return null;
      }
    })();
  }
  return inflight;
}

/**
 * ADR-124 D6/I8 — is this zone's gate EFFECTIVELY open (the platform kill-switch AND the zone's
 * own switch)? FAIL-CLOSED: anything other than a 200 `{ mode: 'open' }` is closed. UX only —
 * the launch route enforces the same gate server-side.
 */
export async function readZoneGate(kind: 'sow' | 'beta', zone: string): Promise<'open' | 'closed'> {
  const path = kind === 'beta' ? '/gate/beta/state' : '/gate/state';
  try {
    const res = await fetch(`${sowGateBase()}${path}?zone=${encodeURIComponent(zone)}`);
    if (!res.ok) return 'closed';
    const body = (await res.json()) as { mode?: unknown };
    return body.mode === 'open' ? 'open' : 'closed';
  } catch {
    return 'closed';
  }
}

/** Test seam — forget the cached map. */
export function __resetZoneMapCache(): void {
  inflight = null;
}

/**
 * The served map as React state for a MOUNT: `undefined` while loading, `null` if it could not be
 * loaded, the map otherwise — the three states the residency step distinguishes.
 */
export function useZoneMap(): ZoneMap | null | undefined {
  const [map, setMap] = useState<ZoneMap | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void loadZoneMap().then((m) => {
      if (live) setMap(m);
    });
    return () => {
      live = false;
    };
  }, []);
  return map;
}
